/**
 * SectionCard — the canonical card surface used to wrap chunks of UI
 * content (forms, lists, settings panels) in Phase-1 of the design
 * refresh.
 *
 * Replaces the previous mix of:
 *   - heavy gradient cards (bg-gradient-to-r from-cyan-500…)
 *   - faux-glass divs (bg-white/80 backdrop-blur)
 *   - random shadow-2xl boxes
 * with ONE quiet shape: white surface, slate-200 1-px border, very
 * subtle 1-px shadow. Visual hierarchy comes from spacing + content,
 * not from competing decorations.
 *
 * Usage:
 *   <SectionCard title="Company details" subtitle="Used on invoices"
 *                icon={Building2} actions={<button>Edit</button>}>
 *     …form…
 *   </SectionCard>
 */
import React from 'react';

export default function SectionCard({
  title,
  subtitle,
  icon: Icon,
  iconTone = 'cyan',        // cyan | emerald | amber | rose | slate
  actions,
  children,
  className = '',
  bodyClassName = 'p-5',
  testid,
}) {
  const iconColor = {
    cyan:    'bg-cyan-50    text-cyan-600    border-cyan-200',
    emerald: 'bg-emerald-50 text-emerald-600 border-emerald-200',
    amber:   'bg-amber-50   text-amber-600   border-amber-200',
    rose:    'bg-rose-50    text-rose-600    border-rose-200',
    slate:   'bg-slate-100  text-slate-600   border-slate-200',
  }[iconTone] || 'bg-slate-100 text-slate-600 border-slate-200';

  const hasHeader = title || subtitle || actions || Icon;

  return (
    <section
      className={`bg-white border border-slate-200 rounded-xl shadow-[0_1px_2px_rgba(15,23,42,0.04)] ${className}`}
      data-testid={testid}
    >
      {hasHeader && (
        <header className="flex items-start justify-between gap-3 flex-wrap px-5 py-3.5 border-b border-slate-100">
          <div className="flex items-start gap-3 min-w-0">
            {Icon && (
              <div className={`w-8 h-8 rounded-lg border flex items-center justify-center flex-shrink-0 ${iconColor}`}>
                <Icon className="w-4 h-4" />
              </div>
            )}
            <div className="min-w-0">
              {title && (
                <h3 className="text-[15px] font-semibold text-slate-900 leading-tight truncate">
                  {title}
                </h3>
              )}
              {subtitle && (
                <p className="text-[12px] text-slate-500 mt-0.5 leading-snug">
                  {subtitle}
                </p>
              )}
            </div>
          </div>
          {actions && <div className="flex items-center gap-2 flex-shrink-0">{actions}</div>}
        </header>
      )}
      <div className={bodyClassName}>{children}</div>
    </section>
  );
}
